import { useCallback, useContext, useRef, useState } from "react";
import { css } from "@emotion/react";
import {
  Box,
  Checkbox,
  DialogActions,
  FormControlLabel,
  Stack,
  Switch,
  TableCell,
  TableContainer,
  TableRow,
} from "@mui/material";
import { Formik } from "formik";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import type { InferType } from "yup";
import { boolean, object, string } from "yup";

import type {
  CreateTeamRequest,
  ExtendedTeam,
  ExtendedThalamosUser,
  TeamMembership,
} from "@thalamos/aspire-common";
import {
  isAdminUser,
  isGuestUser,
  nonEmptyRequiredString,
  teamTypeValues,
} from "@thalamos/aspire-common";

import { api, apiHooks } from "../../api.js";
import { LoggedInUserContext } from "../../Contexts.js";
import { routeFns } from "../../routePaths.js";
import { ConfirmationModal } from "../ConfirmationModal.js";
import { CareHomeGrantsPicker } from "./CareHomeGrantsPicker.js";
import { addTeamMemberValues } from "./constants.js";
import { LocalAuthorityMappingsPicker } from "./LocalAuthorityMappingsPicker.js";
import { OrganisationPicker } from "./OrganisationPicker.js";
import type { MenuOptionsType } from "~/components/design-system/index.js";
import {
  Banner,
  BannerList,
  Button,
  Dropdown,
  FormLabel,
  HelperText,
  LoadingSpinner,
  MenuFlyout,
  PopupDialog,
  PopupDialogTitle,
  renderErrorToast,
  renderSuccessToast,
  TextField,
  Typeahead,
} from "~/components/design-system/index.js";
import { ConfirmationCheckboxFormField } from "~/components/form/index.js";
import type { DialogProps } from "~/hooks/dialogs/index.js";
import { useDialogs } from "~/hooks/dialogs/index.js";
import { getRoleLabel } from "~/util.js";

function DeleteTeamButton({ team }: { team: ExtendedTeam }) {
  const navigate = useNavigate();
  const dialogs = useDialogs();

  return (
    <Button
      onClick={() => {
        const runAsync = async () => {
          const confirmation = await dialogs.open(DeleteTeamDialog);

          if (confirmation.continue) {
            const result = await api.teams.delete(team.id, {
              reason: "created-in-error",
              ignoreWorkItems: confirmation.ignoreWorkItems,
            });

            if (result.status === 204) {
              navigate(routeFns.adminTeamsPage());
              renderSuccessToast({
                message: "Team deleted successfully",
              });
            } else {
              renderErrorToast({
                message: `${result.data.reason}`,
              });
            }
          }
        };
        void runAsync();
      }}
      sx={{ ml: "1em" }}
      color={"error"}
      label={"Delete"}
    />
  );
}

const DeleteTeamDialogSchema = object({
  ignoreWorkItems: boolean().default(false).required(),
});

function DeleteTeamDialog({
  open,
  onClose,
}: DialogProps<
  unknown,
  | ({ continue: true } & InferType<typeof DeleteTeamDialogSchema>)
  | { continue: false }
>) {
  const { t } = useTranslation();

  const handleClose = useCallback(() => {
    void onClose({
      continue: false,
    });
  }, [onClose]);

  const handleContinue = useCallback(
    (values: InferType<typeof DeleteTeamDialogSchema>) => {
      void onClose({
        continue: true,
        ...values,
      });
    },
    [onClose],
  );

  return (
    <PopupDialog open={open} onClose={handleClose}>
      <PopupDialogTitle closeDialog={handleClose} titleText={"Delete Team"} />
      <Stack
        sx={{
          justifyContent: "center",
          alignItems: "center",
          gap: 1,
        }}
      >
        <Banner
          bannerType={BannerList.WARNING}
          body={"Are you sure you want to delete this team? This cannot be undone.\nThe team cannot have any members or any pending/accepted/completed work".split(
            "\n",
          )}
        />
        <Formik
          initialValues={{
            ignoreWorkItems: false,
          }}
          validationSchema={DeleteTeamDialogSchema}
          onSubmit={(values) => {
            handleContinue(values);
          }}
        >
          {(formikData) => {
            const { submitForm } = formikData;

            const fieldProps = {
              ...formikData,
              validationSchema: DeleteTeamDialogSchema,
              context: {},
            };

            return (
              <Stack sx={{ width: "100%", padding: "1rem" }}>
                <ConfirmationCheckboxFormField
                  field={{
                    type: "confirmation-checkbox",
                    checkboxLabel: "Delete even if there are work items",
                    field: "ignoreWorkItems",
                  }}
                  fieldProps={fieldProps}
                />

                <DialogActions
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignSelf: "normal",
                  }}
                >
                  <Button
                    label={t("buttonLabels.cancel")}
                    color="error"
                    variant="outlined"
                    onClick={handleClose}
                  />
                  <Button
                    color="primary"
                    onClick={() => {
                      void submitForm();
                    }}
                    label={t("buttonLabels.continue")}
                  />
                </DialogActions>
              </Stack>
            );
          }}
        </Formik>
      </Stack>
    </PopupDialog>
  );
}

const teamDetailsSchema = object({
  name: nonEmptyRequiredString.default(""),
  type: nonEmptyRequiredString.oneOf(teamTypeValues).default("mha"),
  organisationId: nonEmptyRequiredString.default(""),
  organisationName: string().default(""),
  primaryEmail: nonEmptyRequiredString.email().default(""),
  hasPatientMergeAccess: boolean().default(false),
});

function TeamBasicDetailsCreateOrEdit({
  initialData,
  team,
  onSubmit,
  isPatientMergeAccess,
}: {
  initialData: Partial<CreateTeamRequest>;
  team: ExtendedTeam | null;
  onSubmit: (data: CreateTeamRequest) => void;
  isPatientMergeAccess: boolean | undefined;
}) {
  return (
    <Formik<CreateTeamRequest & { organisationName: string | null }>
      initialValues={{
        ...teamDetailsSchema.getDefault(),
        ...initialData,
        organisationName: team?.organisationName || "",
        hasPatientMergeAccess: team?.hasPatientMergeAccess || false,
      }}
      onSubmit={(data) => {
        return onSubmit({
          ...data,
          organisationName: undefined,
        } as CreateTeamRequest);
      }}
      validationSchema={teamDetailsSchema}
    >
      {({ values, setValues, isSubmitting, touched, errors, submitForm }) => (
        <Box sx={{ maxWidth: "600px", marginBottom: "2em" }}>
          <Box>
            <FormLabel label={"Name"} error={touched.name && !!errors.name} />

            <TextField
              name={"name"}
              useFullWidth={true}
              value={values.name}
              onChange={(e) => setValues({ ...values, name: e })}
            />
            {errors.name && touched.name && (
              <Box sx={{ mb: 4 }}>
                <HelperText errorMessage={errors.name as string} />
              </Box>
            )}
          </Box>

          <Box>
            <FormLabel
              label={"Email"}
              error={touched.primaryEmail && !!errors.primaryEmail}
            />
            <TextField
              name={"email"}
              useFullWidth={true}
              value={values.primaryEmail}
              onChange={(e) => setValues({ ...values, primaryEmail: e })}
            />
            {errors.primaryEmail && touched.primaryEmail && (
              <Box sx={{ mb: 4 }}>
                <HelperText errorMessage={errors.primaryEmail as string} />
              </Box>
            )}
          </Box>

          <Box>
            <FormLabel label={"Type"} error={touched.type && !!errors.type} />
            <Dropdown
              name={"type"}
              selectedValue={values.type}
              values={teamTypeValues
                .filter((v) => v !== "independent" && v !== "guest")
                .map(
                  (value) =>
                    ({
                      value,
                      label: value,
                    }) as const,
                )
                .sort((a, b) => a.label.localeCompare(b.label))}
              onChange={(e) => setValues({ ...values, type: e })}
            />
            {errors.type && touched.type && (
              <Box sx={{ mb: 4 }}>
                <HelperText errorMessage={errors.type as string} />
              </Box>
            )}
          </Box>
          <OrganisationPicker
            label={"Managing Organisation"}
            error={
              touched.organisationId ? (errors.organisationId as string) : null
            }
            idField={"organisationId"}
            nameField={"organisationName"}
            values={values}
            setValues={setValues}
          />
          {isPatientMergeAccess && (
            <Box sx={{ mb: 1 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={values.hasPatientMergeAccess}
                    onChange={() => {
                      setValues({
                        ...values,
                        hasPatientMergeAccess: !values.hasPatientMergeAccess,
                      });
                    }}
                  />
                }
                label={"Patient Merge Enabled"}
              />
            </Box>
          )}
          <Box>
            <>
              <Button
                disabled={isSubmitting}
                label={team ? "Update Team" : "Create Team"}
                onClick={() => {
                  return submitForm();
                }}
              />
              {team && <DeleteTeamButton team={team} />}
            </>
          </Box>
        </Box>
      )}
    </Formik>
  );
}

function TeamMemberRow({
  user,
  team,
  member,
  refetchTeam,
}: {
  user: ExtendedThalamosUser | undefined;
  refetchTeam: () => void;
  team: ExtendedTeam;
  member: ExtendedTeam["members"][0];
}) {
  const cardRef = useRef(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [confirmFn, setConfirmFn] = useState<{
    confirmFn: () => void;
    message: string;
  } | null>(null);
  const isEditableTeam = team.type !== "independent" && team.type !== "guest";

  const [isLoading, setIsLoading] = useState(false);

  const options: MenuOptionsType[] = [
    {
      name: "Remove from Team",
      onClick: () => {
        setConfirmFn({
          confirmFn: async () => {
            await api.teams.removeMember(team.id, {
              userId: member.userId,
              role: member.role,
            });

            refetchTeam();
          },
          message: `Are you sure you want to remove ${member.userName} from ${team.name}?`,
        });
      },
      icon: "delete",
      disabled: false,
    },
  ];

  return (
    <TableRow>
      {confirmFn && (
        <ConfirmationModal
          message={confirmFn.message}
          confirmFn={confirmFn.confirmFn}
          closeFn={() => setConfirmFn(null)}
        />
      )}
      <TableCell>{member.userName}</TableCell>
      <TableCell>{member.userEmail}</TableCell>
      <TableCell>{getRoleLabel(member.role)}</TableCell>
      <TableCell>
        {isLoading ? (
          <LoadingSpinner />
        ) : (
          <Checkbox
            checked={member.hasPatientMergeAccess}
            disabled={!team.hasPatientMergeAccess}
            onChange={async () => {
              setIsLoading(true); // Enable loading state
              try {
                await api.users.update(member.userId, {
                  hasPatientMergeAccess: !member.hasPatientMergeAccess,
                });
                refetchTeam();
              } catch (error) {
                console.error("Error updating user:", error);
              } finally {
                setIsLoading(false);
              }
            }}
          />
        )}
      </TableCell>

      {isAdminUser(user) && isEditableTeam && (
        <TableCell>
          <Box ref={cardRef} onClick={() => setIsMenuOpen(true)}>
            <MenuFlyout
              cardRef={cardRef}
              options={options}
              isOpen={isMenuOpen}
              onClose={() => setIsMenuOpen(false)}
            />
          </Box>
        </TableCell>
      )}
    </TableRow>
  );
}

function TeamCreateEditPageInner({
  teamId,
  team,
  refetchTeam,
}: {
  teamId: string;
  team: ExtendedTeam | null;
  refetchTeam: () => void;
}) {
  const userContext = useContext(LoggedInUserContext);
  const { t } = useTranslation();

  const [searchResults, setSearchResults] = useState<ExtendedThalamosUser[]>(
    [],
  );

  const [userValue, setUserValue] = useState<string | ExtendedThalamosUser>("");

  const isEditableTeam = team?.type !== "independent" && team?.type !== "guest";

  const [{ data: allOrgsAndTeams, loading: allOrgsAndTeamsLoading }] =
    apiHooks.reports.availableOrgsAndTeams({ teamId: teamId });

  const isPatientMergeAccess =
    allOrgsAndTeams?.teamOrgData?.hasPatientMergeAccess;

  const [selectedRole, setSelectedRole] = useState("");

  const handleRoleChange = async (role: TeamMembership["role"]) => {
    if (typeof userValue !== "string" && role) {
      await api.teams.addMember(team!.id, {
        userId: userValue.id,
        role,
      });
      refetchTeam();
    }
  };

  return !allOrgsAndTeamsLoading ? (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        flexDirection: "column",
      }}
    >
      {team ? (
        <h2>
          {team.name} ({team.type})
        </h2>
      ) : (
        <h2>Create New Team</h2>
      )}
      {isEditableTeam ? (
        <TeamBasicDetailsCreateOrEdit
          isPatientMergeAccess={isPatientMergeAccess}
          team={team}
          initialData={
            team
              ? ({
                  name: team.name,
                  type: team.type,
                  organisationId: team.organisationId,
                  primaryEmail: team.primaryEmail,
                } as CreateTeamRequest)
              : {}
          }
          onSubmit={async (data) => {
            if (team) {
              await api.teams.update(team.id, data);
            } else {
              await api.teams.create(teamId, data);
            }
            refetchTeam();
          }}
        />
      ) : (
        <>
          <p>This team cannot be edited</p>
        </>
      )}

      {team && team.type === "section-12-payment-reviewers" && (
        <LocalAuthorityMappingsPicker teamId={team.id} />
      )}

      {team && team.type === "mca" && <CareHomeGrantsPicker teamId={team.id} />}

      {team && <h3>{t("common.members")}</h3>}

      {team && isAdminUser(userContext?.user) && isEditableTeam && (
        <Box sx={{ display: "flex", width: "100%" }}>
          <Typeahead
            css={css`
              width: 500px;
            `}
            label={"Search for a user to add"}
            options={searchResults}
            name="recipientEmail"
            enableFuse={false}
            inputValue={
              typeof userValue === "string" ? userValue : userValue?.email
            }
            optionsKey="name"
            getOptionLabel={(option) =>
              typeof option === "string"
                ? option
                : `${option.name} (${option.email})`
            }
            onInputChange={async (value: string) => {
              const match = searchResults.find(
                (u) => value === `${u.name} (${u.email})`,
              );
              if (match) {
                setUserValue(match);
              } else {
                setUserValue(value);
              }
              if (value.length > 1) {
                const results = await api.users.search(value, 10, 0);

                const filtered = results.data?.results.filter(
                  (u) => !isGuestUser(u),
                );

                setSearchResults(filtered);

                if (match) {
                  setUserValue(match);
                }
              } else {
                setSearchResults([]);
              }
            }}
          />
          <Box sx={{ marginLeft: "1em", width: "20%" }}>
            <Dropdown
              name="add-member-dropdown"
              values={addTeamMemberValues}
              selectedValue={selectedRole}
              onChange={(e) => {
                setSelectedRole(e);
                handleRoleChange(e);
              }}
              placeholder="Select role"
              disabled={typeof userValue === "string"}
            />
          </Box>
        </Box>
      )}

      {team && (
        <TableContainer sx={{ width: "100%" }}>
          <TableRow>
            <TableCell>
              <strong>{t("common.name")}</strong>
            </TableCell>
            <TableCell>
              <strong>{t("common.email")}</strong>
            </TableCell>
            <TableCell>
              <strong>{t("common.role")}</strong>
            </TableCell>
            <TableCell>
              <strong>{"Patient Merge"}</strong>
            </TableCell>
          </TableRow>
          {team &&
            (team?.members || []).map((member) => {
              return (
                <TeamMemberRow
                  refetchTeam={refetchTeam}
                  team={team}
                  member={member}
                  user={userContext?.user}
                />
              );
            })}
        </TableContainer>
      )}
    </Box>
  ) : (
    <></>
  );
}

export function TeamCreateEditPage({}: {}) {
  let { teamId } = useParams();

  const [{ data: team, loading: teamLoading, response }, refetchTeam] =
    apiHooks.teams.get(teamId!);

  return teamLoading ? (
    <></>
  ) : (
    <TeamCreateEditPageInner
      teamId={teamId!}
      team={response?.status === 200 ? team! : null}
      refetchTeam={refetchTeam}
    />
  );
}
